import { useContext, useEffect, useState } from 'react';
import {
    Button,
    Col,
    Row,
    FormGroup,
    Label,
    Input,
    FormFeedback,
    FormText,
    Card,
    CardBody,
    Table,
} from 'reactstrap';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useHistory, useParams, Link } from 'react-router-dom';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { TextInput, PhoneInput } from '~/shared/components/form/Inputs';
import states from '~/shared/states.json';
import { renderIcon } from '~/shared/components/icons';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import { MdContentCopy, MdOutlineDelete, MdEdit } from 'react-icons/md';
import { CampaignContext, CampaignState } from '~/context/campaign';
import {
    fetchMyCampaign,
    postEvent,
    putEvent,
} from '../../../services/campaign';
import toast from 'react-hot-toast';
import { CheckboxInput } from '~/shared/components/form/Inputs';
import {
    Fundraiser,
    FundraiserListItem,
    MyCampaignResponse,
    Registration,
    RegistrationOption,
    RegistrationAddOn,
} from '~/types/campaign';
import { FundraiserImageUpload } from './FundraiserImageUpload';
import { RegistrationOptionModal } from './RegistrationOptionModal';
import { RegistrationAddOnModal } from './RegistrationAddOnModal';
import { FundraiserFlyerUpload } from './FundraiserFlyerUpload';
import { SortableOptionRow } from './SortableOptionRow';
import { getFilename } from '~/services/helper';
import { fetchFundraiser } from '~/services/fundraisers';
import { fetchRegistrations } from '~/services/fundraisers';
import { AuthContext } from '~/context/auth';
import { AdminContext } from '~/context/admin';
import TipTapEditor from '~/shared/components/tiptap/TipTapEditor';

type DefaultEditFundraiserType = typeof defaultEditFundraiser;

const defaultEditFundraiser = {
    fundraiser_type: 'event',
    published: false,
    name: '',
    details: '',
    thank_you_message: '',
    external_link: '',
    is_oneday: true,
    is_multiple_day: false,
    event_date: '',
    event_end_date: '',
    event_time_text: '',
    time: '',
    is_virtual: false,
    event_url: '',
    location: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    i_am_organizer: false,
    organizer_first_name: '',
    organizer_last_name: '',
    organizer_phone: '',
    organizer_email: '',
    requires_registration: false,
    registration_close_date: '',
    maximum_registrants: null,
    call_to_action: 'Register Now',
    event_picture: '',
    flyer: '',
    // registration_questions: [],
    registration_question: '',
    registration_items: [],
    registration_options: [],
    registration_addons: [],
    is_ongoing: false,
    includes_addons: false,
};

export const FundraiserForm = () => {
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [showCampaignPhotoUpload, setShowCampaignPhotoUpload] =
        useState(false);
    const [showFlyerPhotoUpload, setShowFlyerPhotoUpload] = useState(false);
    const [{ campaign }, campaignDispatch]: [CampaignState, any] =
        useContext(CampaignContext);
    const [{ campaignId }, adminDispatch] = useContext(AdminContext);
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;
    const [fundraiserToEdit, setFundraiserToEdit] = useState<
        Fundraiser | undefined
    >(undefined);
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        getValues,
        watch,
        formState: { errors, isSubmitting, dirtyFields, isValid },
    } = useForm({
        defaultValues: defaultEditFundraiser,
        mode: 'all',
    });
    const [registrations, setRegistrations] = useState<Registration[]>([]);

    console.log('errors: ', errors);
    const hasErrors = Object.keys(errors).length > 0;

    const {
        fields: optionFields,
        append: appendOption,
        update: updateOption,
        remove: removeOption,
        move: moveOption,
    } = useFieldArray({
        control,
        name: 'registration_options',
        keyName: '_id',
    });

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleOptionDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = optionFields.findIndex(
            (f: any) => f._id === active.id,
        );
        const newIndex = optionFields.findIndex((f: any) => f._id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        moveOption(oldIndex, newIndex);
    };

    const {
        fields: addonFields,
        append: appendAddon,
        update: updateAddon,
        remove: removeAddon,
    } = useFieldArray({
        control,
        name: 'registration_addons',
        keyName: '_id',
    });

    const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
    const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(
        null,
    );
    const [isAddOnModalOpen, setIsAddOnModalOpen] = useState(false);
    const [editingAddOnIndex, setEditingAddOnIndex] = useState<number | null>(
        null,
    );

    const getButtonText = () => {
        if (isSubmitting) {
            return 'Saving';
        }
        return 'Save';
    };

    const fundraiser_type = watch('fundraiser_type');
    const published = watch('published');
    const is_oneday = watch('is_oneday');
    const is_multiple_day = watch('is_multiple_day');
    const is_virtual = watch('is_virtual');
    const i_am_organizer = watch('i_am_organizer');
    const requires_registration = watch('requires_registration');
    const is_ongoing = watch('is_ongoing');
    const includes_addons = watch('includes_addons');

    const isEvent = fundraiser_type === 'event';
    const isSale = fundraiser_type === 'sale';

    const hasFundraiserLength = is_oneday || is_multiple_day || is_ongoing;

    const eventsUrl = import.meta.env.VITE_EVENT_URL || '';

    const getViewUrl = (
        eventId: number | string,
        wpEventId?: number | null,
        wpSyncUrl?: string,
    ) => {
        if (wpEventId && wpSyncUrl) {
            return `${wpSyncUrl}/?p=${wpEventId}`;
        }
        return `${eventsUrl}/event/${eventId}/signup/`;
    };

    const viewUrl = fundraiserToEdit
        ? getViewUrl(
              fundraiserToEdit.id,
              fundraiserToEdit.wp_event_id,
              fundraiserToEdit.wp_sync_url,
          )
        : '';

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        } else {
            setLoading(false);
        }
    }, []);

    const getRegistrations = async () => {
        try {
            const resp = await fetchRegistrations(id, campaignId);
            setRegistrations(resp.registrations);
        } catch (e) {
            console.log('getRegistrations() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (campaign && !loading) {
            setLoading(true);
            getRegistrations();
        }
    }, [campaign]);

    const onSubmit = async (data: any) => {
        toast.loading('Saving...');
        const payload = { ...data, form_version: 2 };
        if (!is_multiple_day) {
            payload.event_end_date = null;
        }
        if (is_ongoing) {
            // clear dates
            payload.event_date = null;
            payload.event_end_date = null;
        }
        if (Array.isArray(payload.registration_options)) {
            payload.registration_options = payload.registration_options.map(
                (opt: any, i: number) => {
                    const orderChanged =
                        opt.id != null && opt.sort_order !== i;
                    return {
                        ...opt,
                        sort_order: i,
                        isDirty: orderChanged ? true : opt.isDirty,
                    };
                },
            );
        }

        if (isEditing) {
            // PUT to update the event
            payload.id = id;

            const dirtyFieldsArray = Object.keys(dirtyFields);

            if (!dirtyFieldsArray.includes('registration_items')) {
                delete payload.registration_items;
            }

            if (!dirtyFieldsArray.includes('registration_options')) {
                delete payload.registration_options;
            }

            if (!dirtyFieldsArray.includes('registration_addons')) {
                delete payload.registration_addons;
            }

            const updatedFundraiser = await putEvent(
                payload,
                campaign?.campaignDetail.campaign_id,
            );
            toast.dismiss();
            if (updatedFundraiser.detail) {
                if (updatedFundraiser.detail === 'Invalid token.') {
                    localStorage.removeItem('token');
                    history.push('/login');
                    return;
                }
                toast.error(updatedFundraiser.detail);
            } else {
                setFundraiserToEdit(updatedFundraiser);
                const toastViewUrl = getViewUrl(
                    updatedFundraiser.id,
                    updatedFundraiser.wp_event_id,
                    updatedFundraiser.wp_sync_url,
                );
                const viewLabel = updatedFundraiser.wp_event_id
                    ? 'View on campaign page'
                    : 'View your Campaign Fundraiser here!';
                toast.success(
                    <div>
                        Fundraiser updated successfully!{' '}
                        <a href={toastViewUrl} target='_blank'>
                            {viewLabel}
                        </a>
                    </div>,
                );
                // Scroll to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            const newFundraiser = await postEvent(
                payload,
                campaign?.campaignDetail.campaign_id,
            );
            toast.dismiss();
            if (newFundraiser.detail) {
                if (newFundraiser.detail === 'Invalid token.') {
                    localStorage.removeItem('token');
                    history.push('/login');
                    return;
                }
                toast.error(newFundraiser.detail);
            } else {
                // Redirect to the new fundraiser
                setFundraiserToEdit(newFundraiser);
                history.replace(`/fundraisers/v2/edit/${newFundraiser?.id}`);
                const toastViewUrl = getViewUrl(
                    newFundraiser.id,
                    newFundraiser.wp_event_id,
                    newFundraiser.wp_sync_url,
                );
                const viewLabel = newFundraiser.wp_event_id
                    ? 'View on campaign page'
                    : 'View your Campaign Fundraiser here!';
                toast.success(
                    <div>
                        Fundraiser created successfully!{' '}
                        <a href={toastViewUrl} target='_blank'>
                            {viewLabel}
                        </a>
                    </div>,
                );
                // Scroll to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    };

    const getFundraiser = async (id: string) => {
        const fundraiser = await fetchFundraiser(id, campaignId);
        setFundraiserToEdit(fundraiser);
    };

    useEffect(() => {
        if (id) {
            getFundraiser(id);
        }
    }, [id]);

    const getMyCampaign = async () => {
        try {
            if (user?.is_superuser) {
                return;
            }
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (isEditing) {
            return;
        }
        if (i_am_organizer && campaign && 'person' in campaign) {
            const { person } = campaign;
            setValue('organizer_first_name', person?.first_name);
            setValue('organizer_last_name', person?.last_name);
            setValue('organizer_email', person?.email);
            setValue('organizer_phone', person?.phone);
        } else {
            setValue('organizer_first_name', '');
            setValue('organizer_last_name', '');
            setValue('organizer_email', '');
            setValue('organizer_phone', '');
        }
    }, [isEditing, campaign, i_am_organizer]);

    useEffect(() => {
        if (fundraiserToEdit && isEditing) {
            // This is the quickest and most legible way to set the checkboxes
            // If there's an event end date, it's multiple day
            let isMultipleDayCheck = fundraiserToEdit.event_end_date
                ? true
                : false;
            let requireRegistrationCheck =
                !!fundraiserToEdit.registration_close_date ||
                !!fundraiserToEdit.registration_items?.length ||
                !!fundraiserToEdit.registration_options?.length ||
                !!fundraiserToEdit.registration_question;
            let includesAddonsCheck =
                fundraiserToEdit.includes_addons ||
                !!fundraiserToEdit.registration_addons?.length;
            reset({
                ...fundraiserToEdit,
                is_multiple_day: isMultipleDayCheck,
                requires_registration: requireRegistrationCheck,
                includes_addons: includesAddonsCheck,
                is_oneday: !isMultipleDayCheck && !fundraiserToEdit.is_ongoing,
                // i_am_organizer: false,
            } as unknown as DefaultEditFundraiserType);
        }
    }, [fundraiserToEdit]);
    // console.log('fundraiserToEdit: ', fundraiserToEdit);

    return (
        <div className='container__inner'>
            <div className='mb-5'>
                <Row>
                    <Col xl={7}>
                        <h1 className='h2'>
                            {fundraiserToEdit ? 'Edit' : 'Add'} Fundraiser
                        </h1>
                        <p>
                            Let's get people excited about your upcoming
                            fundraiser! To get started, tell us a little about
                            the fundraiser page you'd like to create.
                        </p>
                    </Col>
                    <Col
                        xl={5}
                        className='d-none d-md-flex justify-content-xl-end align-items-start'
                    >
                        {fundraiserToEdit && (
                            <Button
                                color='link'
                                className='px-0'
                                onClick={() => history.push('/fundraisers')}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        )}
                        {fundraiserToEdit ? (
                            <Button
                                tag='a'
                                href={viewUrl}
                                target='_blank'
                                color='outline-dark'
                            >
                                View
                            </Button>
                        ) : (
                            <Button
                                onClick={() => history.push('/fundraisers')}
                                color='outline-dark'
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            disabled={
                                isSubmitting ||
                                !hasFundraiserLength ||
                                hasErrors
                            }
                            color='primary'
                        >
                            {getButtonText()}
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Fundraiser Type Selector */}
            <Row className='mb-5'>
                <Col lg={7}>
                    <h5>What kind of Fundraiser is this?</h5>
                    <Row className='g-3'>
                        <Col md={6}>
                            <Card
                                className='h-100'
                                style={{
                                    cursor: 'pointer',
                                    border: isEvent
                                        ? '2px solid #B5B84B'
                                        : '1px solid #dee2e6',
                                    backgroundColor: isEvent
                                        ? '#B5B84B'
                                        : 'white',
                                    borderRadius: '12px',
                                }}
                                onClick={() =>
                                    setValue('fundraiser_type', 'event')
                                }
                            >
                                <CardBody>
                                    <h5 className={isEvent ? 'text-dark' : ''}>
                                        <strong>Event</strong>
                                    </h5>
                                    <p
                                        className={`mb-2 ${isEvent ? 'text-dark' : 'text-muted'}`}
                                    >
                                        An event has a set date or dates and a
                                        corresponding start time. It can be
                                        virtual or in person.
                                    </p>
                                    <p
                                        className={`mb-0 ${isEvent ? 'text-dark' : 'text-muted'}`}
                                    >
                                        Registration is optional as well as
                                        adding items for sale.
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card
                                className='h-100'
                                style={{
                                    cursor: 'pointer',
                                    border: isSale
                                        ? '2px solid #B5B84B'
                                        : '1px solid #dee2e6',
                                    backgroundColor: isSale
                                        ? '#B5B84B'
                                        : 'white',
                                    borderRadius: '12px',
                                }}
                                onClick={() => {
                                    setValue('fundraiser_type', 'sale');
                                    setValue('requires_registration', false);
                                    setValue('includes_addons', true);
                                }}
                            >
                                <CardBody>
                                    <h5 className={isSale ? 'text-dark' : ''}>
                                        <strong>Sale</strong>
                                    </h5>
                                    <p
                                        className={`mb-2 ${isSale ? 'text-dark' : 'text-muted'}`}
                                    >
                                        A sale could be limited to a date and
                                        time or be ongoing. For example, selling
                                        t-shirts or cookies.
                                    </p>
                                    <p
                                        className={`mb-0 ${isSale ? 'text-dark' : 'text-muted'}`}
                                    >
                                        Registration is not a part of a sale.
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <hr className='my-5' />

            <Row>
                <Col lg={7}>
                    <CheckboxInput
                        name='published'
                        label='Display Fundraiser on the Campaign Page'
                        control={control}
                        // value={published}
                        // onChange={() => {
                        //   setValue('published', !published);
                        // }}
                    />
                    <h5>WHAT</h5>
                    <TextInput
                        name='name'
                        label='Fundraiser Title'
                        control={control}
                        type='text'
                        required={true}
                        ariaRequired={true}
                    />
                    {/* <TextInput
                        name='details'
                        label='Description'
                        control={control}
                        type='textarea'
                        required={true}
                        ariaRequired={true}
                    /> */}
                    <FormGroup>
                        <Label>
                            Description <span className='required'>*</span>
                        </Label>
                        <Controller
                            control={control}
                            name='details'
                            rules={{ required: true }}
                            render={({
                                field: { onChange, value, onBlur },
                            }) => {
                                return (
                                    <TipTapEditor
                                        initialValue={value}
                                        onChange={onChange}
                                    />
                                );
                            }}
                        />
                    </FormGroup>
                    <Row className='g-3 mb-3 align-items-center'>
                        <Col>
                            <FormGroup>
                                <Label>Featured Image</Label>
                                <FundraiserImageUpload
                                    setValue={setValue}
                                    type='event_picture'
                                    accept={{
                                        'image/*': ['.jpeg', '.jpg', '.png'],
                                    }}
                                    savedUrl={watch('event_picture')}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className='g-3 mb-3 align-items-center'>
                        <Col xs='6'>
                            <FormGroup>
                                <Label>Fundraiser Flyer</Label>
                                <Input
                                    readOnly={true}
                                    value={
                                        getFilename(watch('flyer')) ||
                                        'No file selected'
                                    }
                                />
                                <FormText>Accepts PNG, JPG, or PDF</FormText>
                            </FormGroup>
                        </Col>
                        <Col>
                            {watch('flyer') ? (
                                <Button
                                    color='link'
                                    className='text-danger fs-5 mt-2 px-0'
                                    onClick={() => {
                                        setValue('flyer', '');
                                        setShowFlyerPhotoUpload(false);
                                    }}
                                >
                                    {renderIcon('close')}
                                </Button>
                            ) : !showFlyerPhotoUpload ? (
                                <Button
                                    color='outline-dark'
                                    onClick={() =>
                                        setShowFlyerPhotoUpload(true)
                                    }
                                >
                                    <span className='me-2'>
                                        {renderIcon('upload')}
                                    </span>
                                    Upload
                                </Button>
                            ) : null}
                        </Col>
                    </Row>
                    {showFlyerPhotoUpload && !watch('flyer') && (
                        <FundraiserFlyerUpload
                            setValue={setValue}
                            type='flyer'
                            accept={{
                                'image/*': ['.jpeg', '.jpg', '.png'],
                                'application/pdf': [],
                            }}
                            onUploadComplete={() =>
                                setShowFlyerPhotoUpload(false)
                            }
                        />
                    )}
                    {/* <TextInput
            name='event_url'
            label='External Link (Optional)'
            control={control}
            type='url'
            helperText='Add URL if fundraiser tickets or more info is available on a different site. '
          /> */}
                </Col>
                <Col
                    lg={5}
                    className='d-lg-flex justify-content-end align-items-start'
                >
                    {/* <Card
            style={{
              width: '90%',
              background: getDashColor('supporters'),
            }}
          >
            <CardBody>
              <p>Tips for creating a buzz worthy fundraiser:</p>
              <ul>
                <li>One</li>
                <li>Two</li>
                <li>Three</li>
                <li>Four</li>
              </ul>
              <Button
                color='link'
                className='px-0'
                onClick={() => console.log('click')}
              >
                View Example Fundraiser (Helpful Tools)
              </Button>
            </CardBody>
          </Card> */}
                </Col>
            </Row>

            <hr className='my-5' />

            <Row>
                <Col lg={7}>
                    <h5>WHEN</h5>
                    <Label>Fundraiser Length</Label>
                    <FormGroup check inline className='mb-3'>
                        <label className='form-check-label'>
                            One Day
                            <input
                                name='fundraiser_length'
                                id='is_oneday'
                                className='form-check-input'
                                type='radio'
                                checked={is_oneday}
                                onChange={() => {
                                    setValue('is_oneday', !is_oneday);
                                    setValue('is_ongoing', false);
                                    setValue('is_multiple_day', false);
                                }}
                            />
                        </label>
                    </FormGroup>
                    <FormGroup check inline className='mb-3'>
                        <label className='form-check-label'>
                            Multiple Days
                            <input
                                name='fundraiser_length'
                                id='is_multiple_day'
                                className='form-check-input'
                                type='radio'
                                checked={is_multiple_day}
                                onChange={() => {
                                    setValue(
                                        'is_multiple_day',
                                        !is_multiple_day,
                                    );
                                    setValue('is_oneday', false);
                                    setValue('is_ongoing', false);
                                }}
                            />
                        </label>
                    </FormGroup>
                    <FormGroup check inline className='mb-3'>
                        <label className='form-check-label'>
                            Ongoing
                            <input
                                name='fundraiser_length'
                                id='is_ongoing'
                                className='form-check-input'
                                type='radio'
                                checked={is_ongoing}
                                onChange={() => {
                                    setValue('is_ongoing', !is_ongoing);
                                    setValue('is_oneday', false);
                                    setValue('is_multiple_day', false);
                                }}
                            />
                        </label>
                    </FormGroup>

                    {!is_ongoing && (
                        <>
                            <Row>
                                <Col sm={4}>
                                    <TextInput
                                        name='event_date'
                                        label={
                                            is_multiple_day
                                                ? 'Start Date'
                                                : 'Date'
                                        }
                                        control={control}
                                        type='date'
                                        required={is_oneday || is_multiple_day}
                                    />

                                    {is_multiple_day && (
                                        <TextInput
                                            name='event_end_date'
                                            label='End date'
                                            control={control}
                                            type='date'
                                            required={is_multiple_day}
                                        />
                                    )}

                                    <TextInput
                                        name='event_time_text'
                                        label='Time'
                                        control={control}
                                        type='text'
                                        // helperText="HH:MM or 'From 2 Till 7'"
                                        required={false}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}

                    {!hasFundraiserLength && (
                        <div
                            className='invalid-feedback'
                            style={{ display: 'block' }}
                        >
                            Must select a fundraiser length
                        </div>
                    )}
                </Col>
            </Row>

            {!isSale && (
                <>
                    <hr className='my-5' />

                    <Row>
                        <Col lg={7}>
                            <h5>WHERE</h5>
                            <Row>
                                <Col sm={8}>
                                    <CheckboxInput
                                        name='is_virtual'
                                        label='This is a virtual fundraiser'
                                        control={control}
                                        // value={is_virtual}
                                        // onChange={() => {
                                        //   setValue('is_virtual', !is_virtual);
                                        // }}
                                    />
                                    {is_virtual && (
                                        <FormGroup>
                                            <Label>Event URL</Label>
                                            <Controller
                                                name='event_url'
                                                control={control}
                                                rules={{
                                                    pattern: {
                                                        value: /^https?:\/\/.+/,
                                                        message:
                                                            'Please enter a valid URL starting with http:// or https://',
                                                    },
                                                }}
                                                render={({ field }) => (
                                                    <Input
                                                        type='url'
                                                        placeholder='url'
                                                        invalid={
                                                            !!errors.event_url
                                                        }
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {errors.event_url && (
                                                <FormFeedback>
                                                    {errors.event_url.message}
                                                </FormFeedback>
                                            )}
                                            <small className='text-muted'>
                                                If there is no need for an
                                                external URL you can leave this
                                                field blank
                                            </small>
                                        </FormGroup>
                                    )}
                                    {!is_virtual && (
                                        <>
                                            <TextInput
                                                name='location'
                                                label='Fundraiser Location'
                                                control={control}
                                                type='text'
                                            />
                                            <TextInput
                                                name='address_1'
                                                label='Street Address 1'
                                                control={control}
                                                type='text'
                                            />
                                            <TextInput
                                                name='address_2'
                                                label='Street Address 2'
                                                control={control}
                                                type='text'
                                            />
                                            <TextInput
                                                name='city'
                                                label='City'
                                                control={control}
                                                type='text'
                                            />
                                            <Row>
                                                <Col sm={8}>
                                                    <FormGroup>
                                                        <Label>State</Label>
                                                        <Input
                                                            type='select'
                                                            name='state'
                                                            id='state'
                                                            value={watch(
                                                                'state',
                                                            )}
                                                            defaultValue={getValues(
                                                                'state',
                                                            )}
                                                            onChange={(e) =>
                                                                setValue(
                                                                    'state',
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        >
                                                            <option
                                                                value={
                                                                    undefined
                                                                }
                                                            >
                                                                Select
                                                            </option>
                                                            {Object.entries(
                                                                states,
                                                            ).map(
                                                                (
                                                                    [
                                                                        accronym,
                                                                        label,
                                                                    ],
                                                                    i,
                                                                ) => (
                                                                    <option
                                                                        key={i}
                                                                        value={
                                                                            accronym
                                                                        }
                                                                    >
                                                                        {label}
                                                                    </option>
                                                                ),
                                                            )}
                                                        </Input>
                                                        {errors.state && (
                                                            <FormFeedback>
                                                                {
                                                                    errors.state
                                                                        ?.message
                                                                }
                                                            </FormFeedback>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                                <Col sm={4}>
                                                    <TextInput
                                                        name='zip_code'
                                                        label='Zip Code'
                                                        type='zip'
                                                        control={control}
                                                    />
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </>
            )}

            <hr className='my-5' />

            <Row>
                <Col lg={7}>
                    <h5>WHO TO CONTACT</h5>
                    <CheckboxInput
                        name='i_am_organizer'
                        label='I am the organizer'
                        control={control}
                    />
                    <Row>
                        <Col sm={8}>
                            <TextInput
                                name='organizer_first_name'
                                label='Organizer First Name'
                                control={control}
                                type='text'
                                required={false}
                            />
                            <TextInput
                                name='organizer_last_name'
                                label='Organizer Last Name'
                                control={control}
                                type='text'
                                required={false}
                            />
                            <PhoneInput
                                name='organizer_phone'
                                label='Organizer Phone'
                                control={control}
                                required={false}
                            />
                            <TextInput
                                name='organizer_email'
                                label='Organizer Email'
                                control={control}
                                type='email'
                                required={false}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>

            <hr className='my-5' />

            <Row className='mb-5'>
                <Col lg={7}>
                    {isEvent && (
                        <>
                            <h5>REGISTRATION</h5>

                            <CheckboxInput
                                name='requires_registration'
                                label='This fundraiser requires online registration'
                                control={control}
                                // value={requires_registration}
                                onClick={() => {
                                    // setValue('requires_registration', !requires_registration);
                                    setTimeout(() => {
                                        // amount to scroll is negative to scroll up
                                        window.scrollBy({
                                            top: 600,
                                            behavior: 'smooth',
                                        });
                                    }, 400);
                                }}
                            />

                            {requires_registration && (
                                <>
                                    <Row>
                                        <Col sm={4}>
                                            <TextInput
                                                name='registration_close_date'
                                                label='Registration Close Date'
                                                control={control}
                                                type='date'
                                                required={!is_ongoing}
                                            />
                                        </Col>
                                        <Col sm={4}>
                                            <FormGroup>
                                                <Label>
                                                    <strong>
                                                        Maximum Number of
                                                        Registrations
                                                    </strong>
                                                </Label>
                                                <Controller
                                                    name='maximum_registrants'
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type='number'
                                                            min='0'
                                                            placeholder='000'
                                                            {...field}
                                                            onChange={(e) => {
                                                                const val =
                                                                    e.target
                                                                        .value;
                                                                field.onChange(
                                                                    val === ''
                                                                        ? null
                                                                        : Number(
                                                                              val,
                                                                          ),
                                                                );
                                                            }}
                                                            value={
                                                                field.value ??
                                                                ''
                                                            }
                                                            style={{
                                                                width: '120px',
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <small className='text-muted'>
                                                    This may be used to limit
                                                    the amount of tickets that
                                                    can be sold for an event
                                                </small>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <h5 className='mt-4'>OPTIONS</h5>
                                    <p className='text-muted'>
                                        Registration options are required for
                                        people to sign up, otherwise just the
                                        flyer or description of event will be
                                        shown
                                    </p>

                                    {optionFields.length === 0 ? (
                                        <Card
                                            className='mb-3'
                                            style={{ background: '#F0F6F6' }}
                                        >
                                            <CardBody className='text-center py-4'>
                                                <strong>
                                                    You don't have any
                                                    registration options yet
                                                </strong>
                                                <p className='text-muted mb-0'>
                                                    This is where you can see
                                                    any of the options that you
                                                    have created for this
                                                    fundraiser. Add your first
                                                    option here.
                                                </p>
                                            </CardBody>
                                        </Card>
                                    ) : (
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleOptionDragEnd}
                                        >
                                            <Table
                                                borderless
                                                className='mb-3 align-middle'
                                            >
                                                <thead>
                                                    <tr
                                                        style={{
                                                            borderBottom:
                                                                '1px solid #dee2e6',
                                                        }}
                                                    >
                                                        <th
                                                            style={{ width: 32 }}
                                                        ></th>
                                                        <th
                                                            className='fw-bold small text-dark'
                                                            style={{
                                                                width: '38%',
                                                            }}
                                                        >
                                                            Name
                                                        </th>
                                                        <th
                                                            className='fw-bold small text-dark'
                                                            style={{
                                                                width: '23%',
                                                            }}
                                                        >
                                                            Question
                                                        </th>
                                                        <th
                                                            className='fw-bold small text-dark text-center'
                                                            style={{
                                                                width: '10%',
                                                            }}
                                                        >
                                                            Quantity
                                                        </th>
                                                        <th
                                                            className='fw-bold small text-dark text-end'
                                                            style={{
                                                                width: '10%',
                                                            }}
                                                        >
                                                            Price
                                                        </th>
                                                        <th
                                                            style={{
                                                                width: '15%',
                                                            }}
                                                        ></th>
                                                    </tr>
                                                </thead>
                                                <SortableContext
                                                    items={optionFields.map(
                                                        (f: any) => f._id,
                                                    )}
                                                    strategy={
                                                        verticalListSortingStrategy
                                                    }
                                                >
                                                    <tbody>
                                                        {optionFields.map(
                                                            (option, index) => (
                                                                <SortableOptionRow
                                                                    key={
                                                                        option._id
                                                                    }
                                                                    id={
                                                                        option._id
                                                                    }
                                                                    option={
                                                                        option as any
                                                                    }
                                                                    index={index}
                                                                    onEdit={(
                                                                        i,
                                                                    ) => {
                                                                        setEditingOptionIndex(
                                                                            i,
                                                                        );
                                                                        setIsOptionModalOpen(
                                                                            true,
                                                                        );
                                                                    }}
                                                                    onToggleVisible={(
                                                                        i,
                                                                    ) => {
                                                                        const o =
                                                                            optionFields[
                                                                                i
                                                                            ] as any;
                                                                        updateOption(
                                                                            i,
                                                                            {
                                                                                ...o,
                                                                                is_visible:
                                                                                    o.is_visible ===
                                                                                    false,
                                                                            },
                                                                        );
                                                                    }}
                                                                    onDuplicate={(
                                                                        i,
                                                                    ) => {
                                                                        setEditingOptionIndex(
                                                                            null,
                                                                        );
                                                                        const {
                                                                            _id,
                                                                            id: _optId,
                                                                            ...rest
                                                                        } = optionFields[
                                                                            i
                                                                        ] as any;
                                                                        appendOption(
                                                                            {
                                                                                ...rest,
                                                                                name: `${rest.name} (copy)`,
                                                                            } as RegistrationOption,
                                                                        );
                                                                        toast.success(
                                                                            'Option duplicated',
                                                                        );
                                                                    }}
                                                                    onRemove={(
                                                                        i,
                                                                    ) =>
                                                                        removeOption(
                                                                            i,
                                                                        )
                                                                    }
                                                                />
                                                            ),
                                                        )}
                                                    </tbody>
                                                </SortableContext>
                                            </Table>
                                        </DndContext>
                                    )}

                                    <Button
                                        color='outline-dark'
                                        onClick={() => {
                                            setEditingOptionIndex(null);
                                            setIsOptionModalOpen(true);
                                        }}
                                    >
                                        Create Option
                                    </Button>

                                    <RegistrationOptionModal
                                        isOpen={isOptionModalOpen}
                                        onClose={() => {
                                            setIsOptionModalOpen(false);
                                            setEditingOptionIndex(null);
                                        }}
                                        onSave={(optionData) => {
                                            if (editingOptionIndex !== null) {
                                                updateOption(
                                                    editingOptionIndex,
                                                    optionData as RegistrationOption,
                                                );
                                            } else {
                                                appendOption(
                                                    optionData as RegistrationOption,
                                                );
                                            }
                                        }}
                                        option={
                                            editingOptionIndex !== null
                                                ? (optionFields[
                                                      editingOptionIndex
                                                  ] as RegistrationOption)
                                                : undefined
                                        }
                                    />

                                    <h5 className='mt-4'>CALL TO ACTION</h5>
                                    <p className='text-muted'>
                                        This is the label on the button seen
                                        below registration items. This may be
                                        worth changing if a registration item is
                                        a purchasable item like a t-shirt or if
                                        you are promoting sponsorships for an
                                        event.
                                    </p>
                                    <FormGroup>
                                        <Controller
                                            name='call_to_action'
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <FormGroup
                                                        check
                                                        className='mb-2'
                                                    >
                                                        <Input
                                                            type='radio'
                                                            id='cta_register'
                                                            checked={
                                                                field.value ===
                                                                'Register Now'
                                                            }
                                                            onChange={() =>
                                                                field.onChange(
                                                                    'Register Now',
                                                                )
                                                            }
                                                        />
                                                        <Label
                                                            check
                                                            for='cta_register'
                                                            className='ms-2'
                                                        >
                                                            <strong>
                                                                Register Now
                                                                (default)
                                                            </strong>
                                                        </Label>
                                                    </FormGroup>
                                                    <FormGroup
                                                        check
                                                        className='mb-2'
                                                    >
                                                        <Input
                                                            type='radio'
                                                            id='cta_sponsor'
                                                            checked={
                                                                field.value ===
                                                                'Sponsor Now'
                                                            }
                                                            onChange={() =>
                                                                field.onChange(
                                                                    'Sponsor Now',
                                                                )
                                                            }
                                                        />
                                                        <Label
                                                            check
                                                            for='cta_sponsor'
                                                            className='ms-2'
                                                        >
                                                            Sponsor Now
                                                        </Label>
                                                    </FormGroup>
                                                    <FormGroup
                                                        check
                                                        className='mb-2'
                                                    >
                                                        <Input
                                                            type='radio'
                                                            id='cta_buy'
                                                            checked={
                                                                field.value ===
                                                                'Buy Now'
                                                            }
                                                            onChange={() =>
                                                                field.onChange(
                                                                    'Buy Now',
                                                                )
                                                            }
                                                        />
                                                        <Label
                                                            check
                                                            for='cta_buy'
                                                            className='ms-2'
                                                        >
                                                            Buy Now
                                                        </Label>
                                                    </FormGroup>
                                                </>
                                            )}
                                        />
                                    </FormGroup>

                                    <hr className='my-4' />

                                    <h2 className='h4'>Add-ons</h2>
                                    <p className='text-muted'>
                                        Add-ons can be anything from t-shirts of
                                        varying sizes to cookies.
                                    </p>
                                    <FormGroup check className='mb-3'>
                                        <Controller
                                            name='includes_addons'
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type='checkbox'
                                                    id='includes_addons'
                                                    checked={field.value}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            e.target.checked,
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        <Label
                                            check
                                            for='includes_addons'
                                            className='ms-2'
                                        >
                                            <strong>
                                                Include add-ons for sale
                                            </strong>
                                        </Label>
                                    </FormGroup>

                                    {includes_addons && (
                                        <>
                                            {addonFields.length === 0 ? (
                                                <Card
                                                    className='mb-3'
                                                    style={{
                                                        background: '#F0F6F6',
                                                    }}
                                                >
                                                    <CardBody className='text-center py-4'>
                                                        <strong>
                                                            You don't have any
                                                            add-ons yet
                                                        </strong>
                                                        <p className='text-muted mb-0'>
                                                            This is where you
                                                            can see any of the
                                                            items that you have
                                                            created for this
                                                            fundraiser. Add your
                                                            first item here.
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            ) : (
                                                <Table
                                                    borderless
                                                    className='mb-3 align-middle'
                                                >
                                                    <thead>
                                                        <tr
                                                            style={{
                                                                borderBottom:
                                                                    '1px solid #dee2e6',
                                                            }}
                                                        >
                                                            <th
                                                                className='fw-bold small text-dark ps-0'
                                                                style={{
                                                                    width: '35%',
                                                                }}
                                                            >
                                                                Name
                                                            </th>
                                                            <th
                                                                className='fw-bold small text-dark'
                                                                style={{
                                                                    width: '20%',
                                                                }}
                                                            >
                                                                Options
                                                            </th>
                                                            <th
                                                                className='fw-bold small text-dark text-center'
                                                                style={{
                                                                    width: '10%',
                                                                }}
                                                            >
                                                                Quantity
                                                            </th>
                                                            <th
                                                                className='fw-bold small text-dark text-end'
                                                                style={{
                                                                    width: '10%',
                                                                }}
                                                            >
                                                                Price
                                                            </th>
                                                            <th
                                                                style={{
                                                                    width: '15%',
                                                                }}
                                                            ></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {addonFields.map(
                                                            (addon, index) => {
                                                                const item =
                                                                    addon as any;
                                                                const qty =
                                                                    item.quantity !=
                                                                    null
                                                                        ? Number(
                                                                              item.quantity,
                                                                          )
                                                                        : null;
                                                                const remaining =
                                                                    item.quantity_remaining !=
                                                                    null
                                                                        ? Number(
                                                                              item.quantity_remaining,
                                                                          )
                                                                        : null;
                                                                const qtyDisplay =
                                                                    qty != null
                                                                        ? remaining !=
                                                                          null
                                                                            ? `${remaining}/${qty}`
                                                                            : `${qty}`
                                                                        : '';
                                                                return (
                                                                    <tr
                                                                        key={
                                                                            addon._id
                                                                        }
                                                                        style={{
                                                                            borderBottom:
                                                                                '1px solid #dee2e6',
                                                                        }}
                                                                    >
                                                                        <td className='ps-0'>
                                                                            <div className='d-flex align-items-start gap-2'>
                                                                                {item.image && (
                                                                                    <img
                                                                                        src={
                                                                                            item.image
                                                                                        }
                                                                                        alt=''
                                                                                        className='rounded'
                                                                                        style={{
                                                                                            width: 48,
                                                                                            height: 48,
                                                                                            objectFit:
                                                                                                'cover',
                                                                                            flexShrink: 0,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                <div>
                                                                                    <strong
                                                                                        className='text-primary d-block'
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setEditingAddOnIndex(
                                                                                                index,
                                                                                            );
                                                                                            setIsAddOnModalOpen(
                                                                                                true,
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            item.name
                                                                                        }
                                                                                    </strong>
                                                                                    {item.description && (
                                                                                        <small
                                                                                            className='text-muted d-block'
                                                                                            style={{
                                                                                                lineHeight: 1.3,
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                item.description
                                                                                            }
                                                                                        </small>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className='text-muted'>
                                                                            {item.options ||
                                                                                ''}
                                                                        </td>
                                                                        <td className='text-center'>
                                                                            {
                                                                                qtyDisplay
                                                                            }
                                                                        </td>
                                                                        <td className='text-end'>
                                                                            $
                                                                            {Number(
                                                                                item.price ||
                                                                                    0,
                                                                            ).toFixed(
                                                                                2,
                                                                            )}
                                                                        </td>
                                                                        <td className='text-end pe-0'>
                                                                            <div className='d-flex align-items-center justify-content-end gap-2'>
                                                                                <span
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        color: '#01aeb4',
                                                                                        fontSize:
                                                                                            '18px',
                                                                                    }}
                                                                                    title={
                                                                                        item.is_visible !==
                                                                                        false
                                                                                            ? 'Visible'
                                                                                            : 'Hidden'
                                                                                    }
                                                                                    onClick={() =>
                                                                                        updateAddon(index, {
                                                                                            ...item,
                                                                                            is_visible:
                                                                                                item.is_visible ===
                                                                                                false,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    {item.is_visible !==
                                                                                    false ? (
                                                                                        <FaRegEye />
                                                                                    ) : (
                                                                                        <FaRegEyeSlash />
                                                                                    )}
                                                                                </span>
                                                                                <span
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        color: '#01aeb4',
                                                                                        fontSize:
                                                                                            '18px',
                                                                                    }}
                                                                                    title='Copy add-on'
                                                                                    onClick={() => {
                                                                                        setEditingAddOnIndex(
                                                                                            null,
                                                                                        );
                                                                                        const {
                                                                                            _id,
                                                                                            id: _addonId,
                                                                                            ...rest
                                                                                        } = item;
                                                                                        appendAddon(
                                                                                            {
                                                                                                ...rest,
                                                                                                name: `${rest.name} (copy)`,
                                                                                            } as RegistrationAddOn,
                                                                                        );
                                                                                        toast.success(
                                                                                            'Add-on duplicated',
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <MdContentCopy />
                                                                                </span>
                                                                                <span
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        color: '#952e46',
                                                                                        fontSize:
                                                                                            '18px',
                                                                                    }}
                                                                                    title='Delete add-on'
                                                                                    onClick={() =>
                                                                                        removeAddon(
                                                                                            index,
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <MdOutlineDelete />
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            },
                                                        )}
                                                    </tbody>
                                                </Table>
                                            )}

                                            <Button
                                                color='outline-dark'
                                                onClick={() => {
                                                    setEditingAddOnIndex(null);
                                                    setIsAddOnModalOpen(true);
                                                }}
                                            >
                                                Create Add-on
                                            </Button>

                                            <RegistrationAddOnModal
                                                isOpen={isAddOnModalOpen}
                                                onClose={() => {
                                                    setIsAddOnModalOpen(false);
                                                    setEditingAddOnIndex(null);
                                                }}
                                                onSave={(addonData) => {
                                                    if (
                                                        editingAddOnIndex !==
                                                        null
                                                    ) {
                                                        updateAddon(
                                                            editingAddOnIndex,
                                                            addonData as RegistrationAddOn,
                                                        );
                                                    } else {
                                                        appendAddon(
                                                            addonData as RegistrationAddOn,
                                                        );
                                                    }
                                                }}
                                                addon={
                                                    editingAddOnIndex !== null
                                                        ? (addonFields[
                                                              editingAddOnIndex
                                                          ] as RegistrationAddOn)
                                                        : undefined
                                                }
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* Sale-specific: Add-ons without registration */}
                    {isSale && (
                        <>
                            <h5>ITEMS FOR SALE</h5>
                            <p className='text-muted'>
                                Add items for sale in your fundraiser.
                            </p>

                            {addonFields.length === 0 ? (
                                <Card
                                    className='mb-3'
                                    style={{ background: '#F0F6F6' }}
                                >
                                    <CardBody className='text-center py-4'>
                                        <strong>
                                            You don't have any items yet
                                        </strong>
                                        <p className='text-muted mb-0'>
                                            This is where you can see any of the
                                            items that you have created for this
                                            fundraiser. Add your first item
                                            here.
                                        </p>
                                    </CardBody>
                                </Card>
                            ) : (
                                <Table borderless className='mb-3 align-middle'>
                                    <thead>
                                        <tr
                                            style={{
                                                borderBottom:
                                                    '1px solid #dee2e6',
                                            }}
                                        >
                                            <th
                                                className='fw-bold small text-dark ps-0'
                                                style={{ width: '35%' }}
                                            >
                                                Name
                                            </th>
                                            <th
                                                className='fw-bold small text-dark'
                                                style={{ width: '20%' }}
                                            >
                                                Options
                                            </th>
                                            <th
                                                className='fw-bold small text-dark text-center'
                                                style={{ width: '10%' }}
                                            >
                                                Quantity
                                            </th>
                                            <th
                                                className='fw-bold small text-dark text-end'
                                                style={{ width: '10%' }}
                                            >
                                                Price
                                            </th>
                                            <th style={{ width: '15%' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {addonFields.map((addon, index) => {
                                            const item = addon as any;
                                            const qty =
                                                item.quantity != null
                                                    ? Number(item.quantity)
                                                    : null;
                                            const remaining =
                                                item.quantity_remaining != null
                                                    ? Number(
                                                          item.quantity_remaining,
                                                      )
                                                    : null;
                                            const qtyDisplay =
                                                qty != null
                                                    ? remaining != null
                                                        ? `${remaining}/${qty}`
                                                        : `${qty}`
                                                    : '';
                                            return (
                                                <tr
                                                    key={addon._id}
                                                    style={{
                                                        borderBottom:
                                                            '1px solid #dee2e6',
                                                    }}
                                                >
                                                    <td className='ps-0'>
                                                        <div className='d-flex align-items-start gap-2'>
                                                            {item.image && (
                                                                <img
                                                                    src={
                                                                        item.image
                                                                    }
                                                                    alt=''
                                                                    className='rounded'
                                                                    style={{
                                                                        width: 48,
                                                                        height: 48,
                                                                        objectFit:
                                                                            'cover',
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                            )}
                                                            <div>
                                                                <strong
                                                                    className='text-primary d-block'
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    onClick={() => {
                                                                        setEditingAddOnIndex(
                                                                            index,
                                                                        );
                                                                        setIsAddOnModalOpen(
                                                                            true,
                                                                        );
                                                                    }}
                                                                >
                                                                    {item.name}
                                                                </strong>
                                                                {item.description && (
                                                                    <small
                                                                        className='text-muted d-block'
                                                                        style={{
                                                                            lineHeight: 1.3,
                                                                        }}
                                                                    >
                                                                        {
                                                                            item.description
                                                                        }
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className='text-muted'>
                                                        {item.options || ''}
                                                    </td>
                                                    <td className='text-center'>
                                                        {qtyDisplay}
                                                    </td>
                                                    <td className='text-end'>
                                                        $
                                                        {Number(
                                                            item.price || 0,
                                                        ).toFixed(2)}
                                                    </td>
                                                    <td className='text-end pe-0'>
                                                        <div className='d-flex align-items-center justify-content-end gap-2'>
                                                            <span
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: '#01aeb4',
                                                                    fontSize:
                                                                        '18px',
                                                                }}
                                                                title={
                                                                    item.is_visible !==
                                                                    false
                                                                        ? 'Visible'
                                                                        : 'Hidden'
                                                                }
                                                                onClick={() =>
                                                                    updateAddon(index, {
                                                                        ...item,
                                                                        is_visible:
                                                                            item.is_visible ===
                                                                            false,
                                                                    })
                                                                }
                                                            >
                                                                {item.is_visible !==
                                                                false ? (
                                                                    <FaRegEye />
                                                                ) : (
                                                                    <FaRegEyeSlash />
                                                                )}
                                                            </span>
                                                            <span
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: '#01aeb4',
                                                                    fontSize:
                                                                        '18px',
                                                                }}
                                                                title='Copy item'
                                                                onClick={() => {
                                                                    setEditingAddOnIndex(
                                                                        null,
                                                                    );
                                                                    const {
                                                                        _id,
                                                                        id: _addonId,
                                                                        ...rest
                                                                    } = item;
                                                                    appendAddon(
                                                                        {
                                                                            ...rest,
                                                                            name: `${rest.name} (copy)`,
                                                                        } as RegistrationAddOn,
                                                                    );
                                                                    toast.success(
                                                                        'Item duplicated',
                                                                    );
                                                                }}
                                                            >
                                                                <MdContentCopy />
                                                            </span>
                                                            <span
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: '#952e46',
                                                                    fontSize:
                                                                        '18px',
                                                                }}
                                                                title='Delete item'
                                                                onClick={() =>
                                                                    removeAddon(
                                                                        index,
                                                                    )
                                                                }
                                                            >
                                                                <MdOutlineDelete />
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}

                            <Button
                                color='outline-dark'
                                onClick={() => {
                                    setEditingAddOnIndex(null);
                                    setIsAddOnModalOpen(true);
                                }}
                            >
                                Create Item
                            </Button>

                            <RegistrationAddOnModal
                                isOpen={isAddOnModalOpen}
                                onClose={() => {
                                    setIsAddOnModalOpen(false);
                                    setEditingAddOnIndex(null);
                                }}
                                onSave={(addonData) => {
                                    if (editingAddOnIndex !== null) {
                                        updateAddon(
                                            editingAddOnIndex,
                                            addonData as RegistrationAddOn,
                                        );
                                    } else {
                                        appendAddon(
                                            addonData as RegistrationAddOn,
                                        );
                                    }
                                }}
                                addon={
                                    editingAddOnIndex !== null
                                        ? (addonFields[
                                              editingAddOnIndex
                                          ] as RegistrationAddOn)
                                        : undefined
                                }
                                title={
                                    editingAddOnIndex !== null
                                        ? 'Edit Item'
                                        : 'Create Item'
                                }
                            />
                        </>
                    )}

                    <hr className='my-4' />

                    <h2 className='h4'>Thank You Message</h2>
                    <p className='text-muted'>
                        Write a custom message to thank users for{' '}
                        {isEvent ? 'registering for' : 'purchasing from'} your
                        fundraiser.
                    </p>
                    <FormGroup>
                        <Label>
                            <strong>Message</strong>
                        </Label>
                        <Controller
                            name='thank_you_message'
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type='textarea'
                                    rows={5}
                                    placeholder='Text'
                                    {...field}
                                />
                            )}
                        />
                    </FormGroup>

                    {/* <hr className='my-5' />
          <Row>
            <Col>
              <h5>PHOTO GALLERY</h5>
            </Col>

            <Col>
              <Button color='link'>Edit Gallery</Button>
            </Col>
          </Row> */}
                </Col>
                <Col lg={5}>
                    {requires_registration && isEvent && (
                        <Card className='border'>
                            <CardBody>
                                <h4>
                                    Registrations (
                                    {fundraiserToEdit?.registration_total})
                                </h4>
                                <Table className='small mb-4'>
                                    <tbody>
                                        {registrations
                                            .slice(0, 5)
                                            .map((registration, index) => {
                                                const name = `${registration.first_name} ${registration.last_name}`;
                                                return (
                                                    <tr key={index}>
                                                        <td className='ps-0'>
                                                            {name}
                                                        </td>
                                                        <td className='pe-0 fw-bold text-end'>
                                                            {registration.registration_details.map(
                                                                (
                                                                    detail,
                                                                    index,
                                                                ) => {
                                                                    return (
                                                                        <div
                                                                            key={
                                                                                index
                                                                            }
                                                                        >
                                                                            {
                                                                                detail
                                                                                    .item
                                                                                    ?.name
                                                                            }{' '}
                                                                            x
                                                                            {
                                                                                detail.quantity
                                                                            }
                                                                        </div>
                                                                    );
                                                                },
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </Table>
                                <Link
                                    className='btn btn-sm btn-outline-dark'
                                    to={`/fundraisers/registrations/${id}`}
                                >
                                    View All
                                </Link>
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
            <div
                className='sticky-bottom p-4 bg-white'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                {fundraiserToEdit && (
                    <Button
                        color='link'
                        className='px-0'
                        onClick={() => history.goBack()}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                )}
                {fundraiserToEdit ? (
                    <Button
                        tag='a'
                        href={viewUrl}
                        target='_blank'
                        color='outline-dark'
                    >
                        View
                    </Button>
                ) : (
                    <Button
                        onClick={() => history.push('/fundraisers')}
                        color='outline-dark'
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    color='primary'
                    disabled={isSubmitting || !hasFundraiserLength || hasErrors}
                    onClick={() => {
                        handleSubmit(onSubmit, () => {
                            console.log('errors', errors);
                            const errorMessages = Object.entries(errors).map(
                                ([key, value]) => {
                                    return value.message?.replace(
                                        ' is required',
                                        '',
                                    );
                                },
                            );
                            toast.error(
                                `Required fields missing: \n\n` +
                                    errorMessages.join('\n'),
                            );
                        })();
                    }}
                >
                    {getButtonText()}
                </Button>
            </div>
        </div>
    );
};
